import { useEffect, useRef, useState } from 'react'
import { Clapperboard } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FileUploader } from '@/components/tools/file-uploader'
import { FileInfo } from '@/components/tools/file-info'
import { ProgressBar } from '@/components/tools/progress-bar'
import { ResultCard } from '@/components/tools/result-card'
import { ErrorMessage } from '@/components/tools/error-message'
import { useToolStore } from '@/stores/tool-store'
import { downloadBlob } from '@/lib/download'
import { useI18n } from '@/lib/i18n'
import type { MediaStage } from '@cloudtools/wasm'

interface MediaConvertPanelProps<TFormat extends string> {
  accept: readonly string[]
  maxSizeMB: number
  icon: LucideIcon
  formats: readonly { value: TFormat; label: string }[]
  defaultFormat: TFormat
  convert: (
    file: File,
    format: TFormat,
    callbacks: { onProgress: (progress: number) => void; onStage: (stage: MediaStage) => void },
  ) => Promise<Blob>
  makeOutputName: (originalName: string, format: TFormat) => string
}

/**
 * Shared conversion flow for video/audio:
 * pick → choose output format → convert (Worker + WASM) → download.
 */
export function MediaConvertPanel<TFormat extends string>({
  accept,
  maxSizeMB,
  icon: Icon,
  formats,
  defaultFormat,
  convert,
  makeOutputName,
}: MediaConvertPanelProps<TFormat>) {
  const [file, setFile] = useState<File | null>(null)
  const [format, setFormat] = useState<TFormat>(defaultFormat)
  const [stage, setStage] = useState<MediaStage>('loading')
  const [stalled, setStalled] = useState(false)
  const lastProgressAt = useRef(Date.now())
  const { status, progress, error, resultBlob, resultName, selectFile, start, setProgress, finish, fail, reset } =
    useToolStore()
  const { t } = useI18n()

  // If no progress event arrives for a while (e.g. engine download, or
  // ffmpeg without duration info), switch the bar to indeterminate mode.
  useEffect(() => {
    if (status !== 'processing') return
    const timer = setInterval(() => {
      setStalled(Date.now() - lastProgressAt.current > 2500)
    }, 500)
    return () => clearInterval(timer)
  }, [status])

  const handleFile = (selected: File) => {
    setFile(selected)
    selectFile()
  }

  const handleReset = () => {
    setFile(null)
    reset()
  }

  const handleConvert = async () => {
    if (!file) return
    start()
    setStalled(false)
    lastProgressAt.current = Date.now()
    setStage('loading')
    try {
      const blob = await convert(file, format, {
        onProgress: (value) => {
          lastProgressAt.current = Date.now()
          setProgress(value)
        },
        onStage: (nextStage) => {
          lastProgressAt.current = Date.now()
          setStage(nextStage)
        },
      })
      finish(blob, makeOutputName(file.name, format))
    } catch (err) {
      console.error('Conversion failed:', err)
      fail(
        err instanceof Error
          ? err.message
          : t('convertFailed'),
      )
    }
  }

  if (status === 'idle') {
    return <FileUploader accept={accept} maxSizeMB={maxSizeMB} onFileSelect={handleFile} />
  }

  if (status === 'processing') {
    return (
      <div className="rounded-xl border bg-card p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <Icon className="h-5 w-5 text-primary" aria-hidden />
          <p className="font-medium">
            {stage === 'loading' ? t('mediaLoading') : t('mediaConverting')}
          </p>
        </div>
        <ProgressBar
          value={stalled || stage === 'loading' ? null : progress}
          label={
            stage === 'loading'
              ? t('mediaEngineNote')
              : t('mediaBrowserNote')
          }
        />
      </div>
    )
  }

  if (status === 'done' && resultBlob && resultName) {
    return (
      <ResultCard
        fileName={resultName}
        sizeBytes={resultBlob.size}
        onDownload={() => downloadBlob(resultBlob, resultName)}
        onReset={handleReset}
      />
    )
  }

  if (status === 'error') {
    return (
      <div className="space-y-4">
        <ErrorMessage
          message={error ?? t('commonErrorDefault')}
          onRetry={file ? handleConvert : undefined}
        />
        {file && <FileInfo file={file} onRemove={handleReset} />}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {file && <FileInfo file={file} onRemove={handleReset} />}

      <div className="rounded-xl border bg-card p-6 shadow-sm">
        <div className="space-y-2">
          <Label htmlFor="output-format">{t('mediaOutputFormat')}</Label>
          <Select value={format} onValueChange={(value: TFormat) => setFormat(value)}>
            <SelectTrigger id="output-format" className="w-full">
              <SelectValue placeholder={t('mediaSelectFormat')} />
            </SelectTrigger>
            <SelectContent>
              {formats.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button onClick={handleConvert} className="mt-6 w-full" size="lg">
          <Clapperboard className="mr-1" aria-hidden />
          {t('mediaAction')}
        </Button>

        <p className="mt-3 text-center text-xs text-muted-foreground">
          {t('mediaLocalNote')}
        </p>
      </div>
    </div>
  )
}
